import mergeDeep from '@/helpers/deepMerge'
import update, { Spec, CustomCommands } from 'immutability-helper'

const CONFIG_KEY = 'gameConfigs_1'
const DEMO_CONFIG_KEY = 'demoGameConfigs'

export const PLACES = [1, 10, 100, 1000, 10000]

const DEFAULT_SPEED = 1.6
const DEFAULT_LENGTH = 10
const DEFAULT_PLACES = 1 as typeof PLACES[number]
const DEFAULT_WITH_SOUND = true

export enum Mode {
	columns = 'columns',
	digits = 'digits',
	abacus = 'abacus',
}

export enum ADDITION_TYPES {
	elementary = 'elementary',
	small_friends = 'small_friends',
	big_friends = 'big_friends',
	family = 'family',
	anzan = 'anzan',

	// new types
	transition50 = 'fifty',
	transition100 = 'hundred',
}

export const LABELED_ADDITION_TYPES = [
	{ value: ADDITION_TYPES.elementary, label: 'JUST FRIENDS' },
	{ value: ADDITION_TYPES.small_friends, label: 'LITTLE FRIENDS' },
	{ value: ADDITION_TYPES.big_friends, label: 'BIG FRIENDS' },
	{ value: ADDITION_TYPES.family, label: 'FAMILY' },
	{ value: ADDITION_TYPES.anzan, label: 'Anzan' },

	// new types
	{ value: ADDITION_TYPES.transition50, label: 'Transition to 50' },
	{ value: ADDITION_TYPES.transition100, label: 'Transition to 100' },
]

export const LABELED_ADDITION_TYPES_DEMO = LABELED_ADDITION_TYPES.slice(0, 1)

export enum ANZAN_TYPES {
	anzan_random = 'anzan_random',
	anzan_plus = 'anzan_plus',
	anzan_minus = 'anzan_minus',
}

export const DEFAULT_ANZAN_TYPE = ANZAN_TYPES.anzan_random

export const ADDITION_DIGITS = {
	[ADDITION_TYPES.elementary]: [3, 4, 5, 6, 7, 8, 9],
	[ADDITION_TYPES.small_friends]: [4, 3, 2, 1],
	[ADDITION_TYPES.big_friends]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
	[ADDITION_TYPES.family]: [6, 7, 8, 9],
	[ADDITION_TYPES.anzan]: [0], //[6, 7, 8, 9],

	// new types
	[ADDITION_TYPES.transition50]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
	[ADDITION_TYPES.transition100]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
}

export const ADDITION_PRO_DIGITS = {
	[ADDITION_TYPES.elementary]: [3, 4, 5, 6, 7, 8],
	[ADDITION_TYPES.small_friends]: [4, 3, 2, 1],
	[ADDITION_TYPES.big_friends]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
	[ADDITION_TYPES.family]: [6, 7, 8, 9],
	[ADDITION_TYPES.anzan]: [0], //[6, 7, 8, 9],

	// new types
	[ADDITION_TYPES.transition50]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
	[ADDITION_TYPES.transition100]: [9, 8, 7, 6, 5, 4, 3, 2, 1],
}

export const ADDITION_DIGITS_DEMO = [3, 4, 5]

export const getAdditionalDigits = (type: ADDITION_TYPES, places?: number) => {
	let digits = ADDITION_DIGITS[type]

	if (places && type === ADDITION_TYPES.elementary && places >= 10) {
		digits = [0, 1, 2, ...digits]
	}

	return digits
}

export const getAbacusVariants = (places: number) => {
	let digits: any = []

	if (places == 1) {
		digits = [
			[
				[1, 4],
				[1, 5],
				[1, 6],
				[1, 7],
				[1, 8],
				[1, 9],
			],
		]
	}

	if (places == 10) {
		digits = [
			[
				[10, 40],
				[11, 19],
				[20, 29],
				[30, 39],
				[40, 49],
				[50, 59],
				[60, 69],
				[70, 79],
				[80, 89],
				[90, 99],
			],
			[
				[10, 90],
				[10, 29],
				[10, 39],
				[10, 49],
				[10, 59],
				[10, 69],
				[10, 79],
				[10, 89],
				[10, 99],
			],
		]
	}

	if (places == 100) {
		digits = [
			[
				[100, 400],
				[100, 199],
				[200, 299],
				[300, 399],
				[400, 499],
				[500, 599],
				[600, 699],
				[700, 799],
				[800, 899],
				[900, 999],
			],
			[
				[100, 900],
				[100, 299],
				[100, 399],
				[100, 499],
				[100, 599],
				[100, 699],
				[100, 799],
				[100, 899],
				[100, 999],
			],
		]
	}

	if (places == 1000) {
		digits = [
			[
				[1000, 4000],
				[1000, 1999],
				[2000, 2999],
				[3000, 3999],
				[4000, 4999],
				[5000, 5999],
				[6000, 6999],
				[7000, 7999],
				[8000, 8999],
				[9000, 9999],
			],
			[
				[1000, 9000],
				[1000, 2999],
				[1000, 3999],
				[1000, 4999],
				[1000, 5999],
				[1000, 6999],
				[1000, 7999],
				[1000, 8999],
				[1000, 9999],
			],
		]
	}

	if (places == 10000) {
		digits = [
			[
				[10000, 40000],
				[10000, 19999],
				[20000, 29999],
				[30000, 39999],
				[40000, 49999],
				[50000, 59999],
				[60000, 69999],
				[70000, 79999],
				[80000, 89999],
				[90000, 99999],
			],
			[
				[10000, 90000],
				[10000, 29999],
				[10000, 39999],
				[10000, 49999],
				[10000, 59999],
				[10000, 69999],
				[10000, 79999],
				[10000, 89999],
				[10000, 99999],
			],
		]
	}

	return digits
}

export const getAdditionalProDigits = (
	type: ADDITION_TYPES,
	places?: number
) => {
	let digits = ADDITION_PRO_DIGITS[type]

	if (places && type === ADDITION_TYPES.elementary && places >= 10) {
		digits = [0, 1, 2, ...digits]
	}

	if (places && type === ADDITION_TYPES.elementary && places < 10) {
		digits = []
	}

	return digits
}

const DEFAULT_ADDITION_TYPE = ADDITION_TYPES.elementary
const DEFAULT_ADDITION_DIGIT = getAdditionalDigits(DEFAULT_ADDITION_TYPE)[0]

export const defaultGameConfig = {
	language: 'uk',
	addition: {
		speed: DEFAULT_SPEED,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		type: DEFAULT_ADDITION_TYPE,
		digit: DEFAULT_ADDITION_DIGIT,
		withSound: DEFAULT_WITH_SOUND,
		mode: Mode.digits,
	},
	abacusRandom: {
		mode: Mode.abacus,
		length: 1, //DEFAULT_LENGTH,
		speed: DEFAULT_SPEED,
		places: DEFAULT_PLACES,
		type: 'abacus_random_2',
		digit: [1, 4], //getAdditionalDigits(ADDITION_TYPES.big_friends)[0],
	},
	anzan: {
		speed: DEFAULT_SPEED,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		type: DEFAULT_ANZAN_TYPE,
		withSound: DEFAULT_WITH_SOUND,
	},
	division: {
		type: 'divide',
		numbers: [100, 10],
	},
	multiply: {
		type: 'multiply',
		numbers: [],
		places: DEFAULT_PLACES,
	},
	negative: {
		type: 'negative',
		speed: DEFAULT_SPEED,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		withSound: DEFAULT_WITH_SOUND,
	},
	columns: {
		type: DEFAULT_ADDITION_TYPE,
		digit: DEFAULT_ADDITION_DIGIT,
		mode: Mode.columns,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		timerCount: 30,
		isTimerEnabled: true,
		anzan_type: DEFAULT_ANZAN_TYPE,
		users: [''], // minimum one user
	},
}

export const defaultDemoGameConfig = {
	language: 'uk',
	addition: {
		speed: DEFAULT_SPEED,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		type: DEFAULT_ADDITION_TYPE,
		digit: DEFAULT_ADDITION_DIGIT,
		withSound: DEFAULT_WITH_SOUND,
	},
	columns: {
		type: DEFAULT_ADDITION_TYPE,
		digit: DEFAULT_ADDITION_DIGIT,
		mode: Mode.columns,
		length: DEFAULT_LENGTH,
		places: DEFAULT_PLACES,
		users: [''], // minimum one user
	},
}

type GameConfig = typeof defaultGameConfig

export const getConfig = (
	defaultLng?: string,
	fullConfig = true
): GameConfig => {
	const storedConfig = localStorage.getItem(
		fullConfig ? CONFIG_KEY : DEMO_CONFIG_KEY
	)
	if (defaultLng && !storedConfig) {
		updateConfig({ language: () => defaultLng }, fullConfig)
	}

	return mergeDeep(
		// hack for easy pure copy
		JSON.parse(
			JSON.stringify(fullConfig ? defaultGameConfig : defaultDemoGameConfig)
		),
		storedConfig ? JSON.parse(storedConfig) : {}
	)
}

export const updateConfig = <C extends CustomCommands<object> = never>(
	spec: Spec<GameConfig, C>,
	fullConfig = true
) => {
	localStorage.setItem(
		fullConfig ? CONFIG_KEY : DEMO_CONFIG_KEY,
		JSON.stringify(update(getConfig(undefined, fullConfig), spec as any))
	)
}
